import React, { useState, Fragment, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Menu, Transition, Dialog } from '@headlessui/react';
import {
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  DocumentTextIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { generateBillingPdf } from '../../utils/billingPdfGenerator';
import { generateCustomerBillingPdf } from '../../utils/customerBillingPdfGenerator';
import {
  BillingSummaryResponse,
  EngagementBilling,
  EngagementBillingDetail,
  AgentBillingBreakdown,
  BillingRecord,
  BillingStatus,
  BILLING_STATUS_CONFIG,
  CustomerBillingAggregate,
  EngagementFixedHoursBreakdown,
} from '../../types/billing';
import MultiSelectSearchableDropdown, { MultiSelectOption } from '../../components/MultiSelectSearchableDropdown';
import SingleSelectSearchableDropdown, { SingleSelectOption } from '../../components/SingleSelectSearchableDropdown';
import {
  formatBillingCurrency,
  formatBillingHoursDisplay,
  formatBillingMoneyPlain,
  roundHalfUp2,
} from '../../utils/billingRound';
import {
  formatPatchingBillingMonthLabel,
  formatPatchingCsvResourceRows,
  formatPatchingQuarterCycleLabel,
  patchingRowsForEngagementCsvExport
} from '../../utils/patchingBillingPdfSection';
import { isResourceChargeExportRow } from '../../constants/patchingGroups';

function isPatchingEngagementRow(billing: EngagementBilling): boolean {
  return (
    billing.isPatchingEngagement === true ||
    (billing.patchingServices?.length ?? 0) > 0
  );
}

/** Reference-only configured patching amount (not billed). */
function getPatchingReferenceAmount(billing: EngagementBilling): number {
  return billing.patchingReferenceAmount ?? 0;
}

/** Billable patching labor (hours × rate) for Final Bill on patching-only engagements. */
function getPatchingLaborAmount(billing: EngagementBilling): number {
  return billing.patchingTotalAmount ?? 0;
}

function getDisplayFinalBill(billing: EngagementBilling): number {
  if (isPatchingEngagementRow(billing)) {
    return getPatchingLaborAmount(billing);
  }
  return billing.dashboardFinalBill ?? billing.finalBill;
}

function getDetailDisplayFinalBill(detail: EngagementBillingDetail): number {
  if (
    detail.summary.isPatchingEngagement === true ||
    (detail.patchingServices?.length ?? 0) > 0
  ) {
    return detail.summary.patchingTotalAmount ?? 0; // billable labor only
  }
  return detail.summary.dashboardFinalBill ?? detail.summary.finalBill;
}

// Generate month options for the last 12 months
const getMonthOptions = (): SingleSelectOption[] => {
  const options: SingleSelectOption[] = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const id = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    // Show "This Month" indicator for the current month
    const label = i === 0 ? `${monthLabel} (This Month)` : monthLabel;
    options.push({ id, label });
  }
  return options;
};

// Billing display: HALF_UP 2dp (matches backend / Excel / PDF / CSV)
const formatCurrency = formatBillingCurrency;
const formatHours = formatBillingHoursDisplay;

// Get billing period date range (first and last day of month)
const getBillingPeriodDates = (billingPeriod: string) => {
  const [year, month] = billingPeriod.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const formatDate = (date: Date) => {
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
  };
  return {
    fromDate: formatDate(firstDay),
    toDate: formatDate(lastDay),
  };
};

const formatYearMonthLabel = (yearMonth?: string | null) => {
  if (!yearMonth) return '-';
  const [yearStr, monthStr] = yearMonth.split('-');
  const y = Number(yearStr);
  const m = Number(monthStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return yearMonth;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Interface for export row data
interface BillingExportRow {
  fromDate: string;
  toDate: string;
  chargeItemType: string;
  description: string;
  group: string;
  totalHours: string;
  rate: string;
  totalAmount: string;
}

// Generate CSV content from billing data
const generateBillingCSV = (rows: BillingExportRow[]): string => {
  const headers = ['From Date', 'To Date', 'Charge Item Type', 'Description', 'Group', 'Total Hours', 'Rate', 'Total Amount'];
  const csvRows = [headers.join(',')];

  let grandTotal = 0;

  rows.forEach(row => {
    // Parse totalAmount and add to grand total
    const amount = parseFloat(row.totalAmount) || 0;
    grandTotal += amount;

    const escapedRow = [
      row.fromDate,
      row.toDate,
      row.chargeItemType,
      `"${row.description.replace(/"/g, '""')}"`,
      `"${row.group.replace(/"/g, '""')}"`,
      row.totalHours,
      row.rate,
      row.totalAmount,
    ];
    csvRows.push(escapedRow.join(','));
  });

  // Add empty row before total
  csvRows.push('');

  // Add total row
  const totalRow = [
    '', '', '', '', '', '', 'Total',
    formatBillingMoneyPlain(grandTotal),
  ];
  csvRows.push(totalRow.join(','));

  return csvRows.join('\n');
};

// Download CSV file
const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Get billing performance status based on actual vs minimum service fee
const getBillingPerformanceStatus = (actualBilling: number, minimumServiceFee: number) => {
  if (minimumServiceFee <= 0) {
    return { status: 'none', percentage: 100, color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-600', borderColor: 'border-l-gray-400' };
  }

  const percentage = (actualBilling / minimumServiceFee) * 100;

  if (percentage < 50) {
    return {
      status: 'critical',
      percentage,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-l-red-500',
      badgeColor: 'bg-red-100 text-red-800'
    };
  } else if (percentage < 75) {
    return {
      status: 'warning',
      percentage,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700',
      borderColor: 'border-l-yellow-500',
      badgeColor: 'bg-yellow-100 text-yellow-800'
    };
  } else {
    return {
      status: 'healthy',
      percentage,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-l-green-500',
      badgeColor: 'bg-green-100 text-green-800'
    };
  }
};

// Performance indicator badge
const PerformanceBadge: React.FC<{ actualBilling: number; minimumServiceFee: number }> = ({ actualBilling, minimumServiceFee }) => {
  const { percentage, badgeColor, status } = getBillingPerformanceStatus(actualBilling, minimumServiceFee);

  if (status === 'none') return null;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
      {percentage.toFixed(0)}%
    </span>
  );
};

// Rate source badge
const RateSourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const colors: Record<string, string> = {
    engagement_agent: 'bg-green-100 text-green-800',
    group_default: 'bg-blue-100 text-blue-800',
    fallback: 'bg-yellow-100 text-yellow-800',
  };
  const labels: Record<string, string> = {
    engagement_agent: 'Agent Rate',
    group_default: 'Group Default',
    fallback: 'System Default',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[source] || 'bg-gray-100 text-gray-800'}`}>
      {labels[source] || source}
    </span>
  );
};

// Billing Status Badge with Dropdown Actions (using Portal)
const BillingStatusBadge: React.FC<{
  engagementId: string;
  billingPeriod: string;
  status: BillingStatus | null;
  onStatusUpdate: (engagementId: string, newStatus: BillingStatus) => void;
  isUpdating: boolean;
}> = ({ engagementId, billingPeriod: _billingPeriod, status, onStatusUpdate, isUpdating }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right - 192 + window.scrollX, // 192 = w-48 (12rem)
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll
  useEffect(() => {
    const handleScroll = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen]);

  const currentStatus = status || 'DRAFT';
  const config = BILLING_STATUS_CONFIG[currentStatus];

  const handleAction = (newStatus: BillingStatus) => {
    onStatusUpdate(engagementId, newStatus);
    setIsOpen(false);
  };

  const dropdownContent = isOpen && !isUpdating && ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[9999]"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
    >
      <div className="py-1">
        {config.nextStatus && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(config.nextStatus!);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <CheckIcon className="h-4 w-4 text-green-500" />
            {config.nextStatusLabel}
          </button>
        )}
        {config.canRevert && config.revertStatus && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(config.revertStatus!);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <ArrowUturnLeftIcon className="h-4 w-4 text-orange-500" />
            Revert to {BILLING_STATUS_CONFIG[config.revertStatus].label}
          </button>
        )}
        {!config.nextStatus && !config.canRevert && (
          <div className="px-4 py-2 text-sm text-gray-500 italic">
            No actions available
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isUpdating}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border ${config.bgColor} ${config.color} ${config.borderColor} hover:opacity-80 transition-opacity`}
      >
        {isUpdating ? (
          <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
        ) : (
          <>
            {config.label}
            <ChevronDownIcon className="h-3 w-3" />
          </>
        )}
      </button>
      {dropdownContent}
    </div>
  );
};

export default function BillingDashboard() {
  const { canViewEntity, loading: permissionsLoading } = useUserPermissions();
  const { hasAnyRole } = useAuth();
  const canAccessBilling = canViewEntity('billing');
  const canExportAll = hasAnyRole(['admin', 'super_admin', 'finance']);

  // Error modal for billing validation issues (module popup style)
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);

  const showErrorModal = (message: string) => {
    setErrorModalMessage(message);
  };

  const monthOptions = getMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.id || '');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Fetch billing groups for filter dropdown
  const { data: billingGroupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['billingGroups'],
    queryFn: async () => {
      const response = await api.get('/groups/billing-groups');
      return response.data as Array<{ id: string; name: string; description: string; memberCount: number }>;
    },
    enabled: canAccessBilling,
  });

  // Fetch all active users for filter dropdown
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown?active=true');
      return response.data as { users: Array<{ id: string; name: string; email: string; role: { id: string; name: string } }> };
    },
    enabled: canAccessBilling,
  });

  // Transform data for MultiSelectSearchableDropdown
  const groupOptions: MultiSelectOption[] = useMemo(() => {
    if (!billingGroupsData) return [];
    return billingGroupsData.map((group) => ({
      id: group.id,
      label: group.name,
      sublabel: group.description || `${group.memberCount} members`,
    }));
  }, [billingGroupsData]);

  const userOptions: MultiSelectOption[] = useMemo(() => {
    if (!usersData?.users) return [];
    return usersData.users.map((user) => ({
      id: user.id,
      label: user.name,
      sublabel: user.email,
    }));
  }, [usersData]);

  // Fetch monthly summary (unfiltered - only depends on month)
  const { data: monthlySummaryData } = useQuery({
    queryKey: ['billingSummary', selectedMonth],
    queryFn: async () => {
      const params = new URLSearchParams({
        month: selectedMonth,
        limit: '500', // Get all engagements for accurate summary
      });
      const response = await api.get(`/billing?${params.toString()}`);
      return response.data as BillingSummaryResponse;
    },
    enabled: !!selectedMonth && canAccessBilling,
  });

  // Fetch filtered billing data (for the table)
  const { data: billingData, isLoading, error } = useQuery({
    queryKey: ['billingFiltered', selectedMonth, selectedCustomerIds, selectedGroupIds, selectedUserIds],
    queryFn: async () => {
      const params = new URLSearchParams({
        month: selectedMonth,
        limit: '100',
        ...(selectedCustomerIds.length > 0 && { customerIds: selectedCustomerIds.join(',') }),
        ...(selectedGroupIds.length > 0 && { groupIds: selectedGroupIds.join(',') }),
        ...(selectedUserIds.length > 0 && { userIds: selectedUserIds.join(',') }),
      });
      const response = await api.get(`/billing?${params.toString()}`);
      return response.data as BillingSummaryResponse;
    },
    enabled: !!selectedMonth && canAccessBilling,
  });

  /** Service-credit engagement rows last so they sit above the customer billing summary footer. */
  const billingsForTable = useMemo(() => {
    const list = billingData?.billings;
    if (!list?.length) return [];
    return [...list].sort((a, b) => {
      const sa = a.isServiceCreditEngagement ? 1 : 0;
      const sb = b.isServiceCreditEngagement ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return (a.engagementNumber || '').localeCompare(b.engagementNumber || '');
    });
  }, [billingData?.billings]);

  // Fetch customers for filter dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
    enabled: canAccessBilling,
  });

  // Transform customer data for MultiSelectSearchableDropdown
  const customerOptions: MultiSelectOption[] = useMemo(() => {
    if (!customersData?.customers) return [];
    return customersData.customers.map((customer: any) => ({
      id: customer.id,
      label: customer.accountName,
      sublabel: customer.accountShortName || undefined,
    }));
  }, [customersData]);

  // Fetch billing records for the selected month
  const queryClient = useQueryClient();
  const { data: billingRecordsData } = useQuery({
    queryKey: ['billingRecords', selectedMonth],
    queryFn: async () => {
      const response = await api.get(`/billing/records?billingPeriod=${selectedMonth}`);
      return response.data as { records: BillingRecord[]; total: number };
    },
    enabled: !!selectedMonth && canAccessBilling,
  });

  // Create a map of engagement ID to billing record status
  const billingRecordsByEngagement = React.useMemo(() => {
    const map = new Map<string, BillingRecord>();
    billingRecordsData?.records?.forEach((record: BillingRecord) => {
      map.set(record.engagementId, record);
    });
    return map;
  }, [billingRecordsData]);

  // State for tracking which engagement is being updated
  const [updatingEngagementId, setUpdatingEngagementId] = useState<string | null>(null);

  // Mutation for updating billing status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ engagementId, status }: { engagementId: string; status: BillingStatus }) => {
      const response = await api.patch(
        `/billing/records/${engagementId}/${selectedMonth}/status`,
        { status, snapshotBillingData: status === 'SENT_TO_CUSTOMER' }
      );
      return response.data;
    },
    onMutate: ({ engagementId }) => {
      setUpdatingEngagementId(engagementId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingRecords', selectedMonth] });
    },
    onSettled: () => {
      setUpdatingEngagementId(null);
    },
    onError: (error: any) => {
      console.error('Failed to update billing status:', error);
      alert(error?.response?.data?.message || 'Failed to update billing status');
    },
  });

  const handleStatusUpdate = (engagementId: string, newStatus: BillingStatus) => {
    updateStatusMutation.mutate({ engagementId, status: newStatus });
  };

  // Export state - track which engagement is being exported
  const [exportingEngagementId, setExportingEngagementId] = useState<string | null>(null);
  const [exportingPdfEngagementId, setExportingPdfEngagementId] = useState<string | null>(null);
  const [exportingAllCsv, setExportingAllCsv] = useState(false);
  const [exportingAllPdf, setExportingAllPdf] = useState(false);
  const [exportingCustomerCsv, setExportingCustomerCsv] = useState(false);
  const [exportingCustomerPdf, setExportingCustomerPdf] = useState(false);

  /** Match engagement detail + exports to the same group/user filters as the table */
  const appendDetailQueryFilters = useCallback((params: URLSearchParams) => {
    if (selectedGroupIds.length > 0) params.set('groupIds', selectedGroupIds.join(','));
    if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));
  }, [selectedGroupIds, selectedUserIds]);

  // Helper function to build export rows for a single engagement
  const buildExportRows = async (billing: EngagementBilling): Promise<BillingExportRow[]> => {
    const { fromDate, toDate } = getBillingPeriodDates(selectedMonth);
    const exportRows: BillingExportRow[] = [];

    // Fetch detailed data for the engagement
    const params = new URLSearchParams({ month: selectedMonth });
    appendDetailQueryFilters(params);
    const detailResponse = await api.get(`/billing/engagements/${billing.engagementId}?${params.toString()}`);
    const detail: EngagementBillingDetail = detailResponse.data;

    // Add resource_charges rows (one per agent) - first
    // Export only: for Sphera Solutions exclude L1 Support Engineer rows
    const isSpheraExport = (detail.engagement?.customerName || '').trim() === 'Sphera Solutions';
    const agentBreakdownForExport =
      detail.agentBreakdown?.filter((a) =>
        isResourceChargeExportRow(a.groupName, { excludeL1ForSphera: isSpheraExport })
      ) ?? [];
    if (agentBreakdownForExport.length > 0) {
      agentBreakdownForExport.forEach((agent) => {
        exportRows.push({
          fromDate,
          toDate,
          chargeItemType: 'resource_charges',
          description: agent.userName,
          group: agent.groupName,
          totalHours: formatHours(agent.totalHours),
          rate: formatBillingMoneyPlain(agent.hourlyRate),
          totalAmount: formatBillingMoneyPlain(agent.billableAmount),
        });
      });
    }

    // Add service_fee_charges row only when (minimum_fees - actual_hours_effort) > 0
    const minimumFee = detail.summary?.minimumServiceFee || 0;
    const actualHoursEffort = detail.summary?.totalBillableAmount || 0;
    const serviceFeeCharge = roundHalfUp2(minimumFee - actualHoursEffort);

    if (serviceFeeCharge > 0) {
      exportRows.push({
        fromDate,
        toDate,
        chargeItemType: 'service_fee_charges',
        description: 'Service Fee',
        group: 'Service Fee',
        totalHours: '1',
        rate: formatBillingMoneyPlain(serviceFeeCharge),
        totalAmount: formatBillingMoneyPlain(serviceFeeCharge),
      });
    }

    // Add platform_cost row
    if ((detail.summary?.platformCost || 0) > 0) {
      const pc = detail.summary?.platformCost || 0;
      exportRows.push({
        fromDate,
        toDate,
        chargeItemType: 'platform_cost',
        description: 'Platform Cost Charges',
        group: 'Platform Cost',
        totalHours: '1',
        rate: formatBillingMoneyPlain(pc),
        totalAmount: formatBillingMoneyPlain(pc),
      });
    }

    // Add service_flat_charges rows (one per plan)
    if (detail.serviceFlatFeeDetails && detail.serviceFlatFeeDetails.length > 0) {
      detail.serviceFlatFeeDetails.forEach((plan) => {
        exportRows.push({
          fromDate,
          toDate,
          chargeItemType: 'service_flat_charges',
          description: 'Service Flat Charges',
          group: 'Service Flat',
          totalHours: '1',
          rate: formatBillingMoneyPlain(plan.price),
          totalAmount: formatBillingMoneyPlain(plan.price),
        });
      });
    }

    return exportRows;
  };

  // Export all engagements to CSV (one file per engagement)
  const handleExportAllCsv = async () => {
    setExportingAllCsv(true);
    try {
      // Fetch fresh billing data for the selected month
      const params = new URLSearchParams({
        month: selectedMonth,
        limit: '100',
        ...(selectedCustomerIds.length > 0 && { customerIds: selectedCustomerIds.join(',') }),
        ...(selectedGroupIds.length > 0 && { groupIds: selectedGroupIds.join(',') }),
        ...(selectedUserIds.length > 0 && { userIds: selectedUserIds.join(',') }),
      });
      const response = await api.get(`/billing?${params.toString()}`);
      const data = response.data as BillingSummaryResponse;

      if (!data?.billings || data.billings.length === 0) {
        alert('No billing data available to export');
        return;
      }

      for (const billing of data.billings) {
        const exportRows = await buildExportRows(billing);
        if (exportRows.length > 0) {
          const csvContent = generateBillingCSV(exportRows);
          const filename = `billing-${billing.engagementNumber}-${selectedMonth}.csv`;
          downloadCSV(csvContent, filename);
          // Small delay between downloads to prevent browser issues
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      console.error('Error exporting all billing CSVs:', error);
      alert('Failed to export billing data. Please try again.');
    } finally {
      setExportingAllCsv(false);
    }
  };

  // Export all engagements to PDF (one file per engagement)
  const handleExportAllPdf = async () => {
    setExportingAllPdf(true);
    try {
      // Fetch fresh billing data for the selected month
      const params = new URLSearchParams({
        month: selectedMonth,
        limit: '100',
        ...(selectedCustomerIds.length > 0 && { customerIds: selectedCustomerIds.join(',') }),
        ...(selectedGroupIds.length > 0 && { groupIds: selectedGroupIds.join(',') }),
        ...(selectedUserIds.length > 0 && { userIds: selectedUserIds.join(',') }),
      });
      const response = await api.get(`/billing?${params.toString()}`);
      const data = response.data as BillingSummaryResponse;

      if (!data?.billings || data.billings.length === 0) {
        alert('No billing data available to export');
        return;
      }

      for (const billing of data.billings) {
        // Fetch detailed data for the engagement
        const detailParams = new URLSearchParams({ month: selectedMonth });
        appendDetailQueryFilters(detailParams);
        const detailResponse = await api.get(`/billing/engagements/${billing.engagementId}?${detailParams.toString()}`);
        const detail: EngagementBillingDetail = detailResponse.data;

        // Generate PDF
        generateBillingPdf({
          detail,
          billingPeriod: selectedMonth,
        });

        // Small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error exporting all billing PDFs:', error);
      alert('Failed to export billing PDFs. Please try again.');
    } finally {
      setExportingAllPdf(false);
    }
  };

  // Export billing data to PDF for a single engagement
  const handleExportPdf = useCallback(async (billing: EngagementBilling) => {
    setExportingPdfEngagementId(billing.engagementId);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      appendDetailQueryFilters(params);
      const detailResponse = await api.get(`/billing/engagements/${billing.engagementId}?${params.toString()}`);
      const detail: EngagementBillingDetail = detailResponse.data;

      generateBillingPdf({
        detail,
        billingPeriod: selectedMonth,
      });
    } catch (error) {
      console.error('Error exporting billing PDF:', error);
      alert('Failed to export billing PDF. Please try again.');
    } finally {
      setExportingPdfEngagementId(null);
    }
  }, [selectedMonth, appendDetailQueryFilters]);

  // Export billing data to CSV for a single engagement
  const handleExportEngagement = useCallback(async (billing: EngagementBilling) => {
    setExportingEngagementId(billing.engagementId);
    try {
      const { fromDate, toDate } = getBillingPeriodDates(selectedMonth);
      const exportRows: BillingExportRow[] = [];

      const params = new URLSearchParams({ month: selectedMonth });
      appendDetailQueryFilters(params);
      const detailResponse = await api.get(`/billing/engagements/${billing.engagementId}?${params.toString()}`);
      const detail: EngagementBillingDetail = detailResponse.data;

      // Add resource_charges rows (one per agent) - first
      // Export only: for Sphera Solutions exclude L1 Support Engineer rows
      const isSpheraExport = (detail.engagement?.customerName || '').trim() === 'Sphera Solutions';
      const agentBreakdownForExport =
        detail.agentBreakdown?.filter((a) =>
          isResourceChargeExportRow(a.groupName, { excludeL1ForSphera: isSpheraExport })
        ) ?? [];
      if (agentBreakdownForExport.length > 0) {
        agentBreakdownForExport.forEach((agent) => {
          exportRows.push({
            fromDate,
            toDate,
            chargeItemType: 'resource_charges',
            description: agent.userName,
            group: agent.groupName,
            totalHours: formatHours(agent.totalHours),
            rate: formatBillingMoneyPlain(agent.hourlyRate),
            totalAmount: formatBillingMoneyPlain(agent.billableAmount),
          });
        });
      }

      // Add service_fee_charges row only when (minimum_fees - actual_hours_effort) > 0
      const minimumFee = detail.summary?.minimumServiceFee || 0;
      const actualHoursEffort = detail.summary?.totalBillableAmount || 0;
      const serviceFeeCharge = roundHalfUp2(minimumFee - actualHoursEffort);

      if (serviceFeeCharge > 0) {
        exportRows.push({
          fromDate,
          toDate,
          chargeItemType: 'service_fee_charges',
          description: 'Service Fee',
          group: 'Service Fee',
          totalHours: '1',
          rate: formatBillingMoneyPlain(serviceFeeCharge),
          totalAmount: formatBillingMoneyPlain(serviceFeeCharge),
        });
      }

      // Add platform_cost row
      if ((detail.summary?.platformCost || 0) > 0) {
        const pc = detail.summary?.platformCost || 0;
        exportRows.push({
          fromDate,
          toDate,
          chargeItemType: 'platform_cost',
          description: 'Platform Cost Charges',
          group: 'Platform Cost',
          totalHours: '1',
          rate: formatBillingMoneyPlain(pc),
          totalAmount: formatBillingMoneyPlain(pc),
        });
      }

      // Add service_flat_charges rows (one per plan)
      if (detail.serviceFlatFeeDetails && detail.serviceFlatFeeDetails.length > 0) {
        detail.serviceFlatFeeDetails.forEach((plan) => {
          exportRows.push({
            fromDate,
            toDate,
            chargeItemType: 'service_flat_charges',
            description: 'Service Flat Charges',
            group: 'Service Flat',
            totalHours: '1',
            rate: formatBillingMoneyPlain(plan.price),
            totalAmount: formatBillingMoneyPlain(plan.price),
          });
        });
      }

      (detail.patchingServices || [])
        .filter((line) => line.showInPeriod !== false)
        .forEach((line) => {
          const periodFrom = line.quarterCycleStartMonth
            ? getBillingPeriodDates(line.quarterCycleStartMonth).fromDate
            : fromDate;
          const periodTo = line.quarterCycleEndMonth
            ? getBillingPeriodDates(line.quarterCycleEndMonth).toDate
            : toDate;
          patchingRowsForEngagementCsvExport(line, periodFrom, periodTo).forEach((patchRow) => {
            exportRows.push({
              fromDate: periodFrom,
              toDate: periodTo,
              chargeItemType: `patching_${line.patchingType.toLowerCase()}`,
              description: patchRow.description,
              group: patchRow.group,
              totalHours: patchRow.totalHours,
              rate: patchRow.rate,
              totalAmount: patchRow.totalAmount,
            });
          });
        });

      // Generate and download CSV
      const csvContent = generateBillingCSV(exportRows);
      const filename = `billing-${billing.engagementNumber}-${selectedMonth}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error exporting billing data:', error);
      alert('Failed to export billing data. Please try again.');
    } finally {
      setExportingEngagementId(null);
    }
  }, [selectedMonth, appendDetailQueryFilters]);

  // Helper: determine if a single customer is selected for exports
  const selectedCustomerIdForExport = useMemo(() => {
    return selectedCustomerIds.length === 1 ? selectedCustomerIds[0] : null;
  }, [selectedCustomerIds]);

  // Customer export buttons should be shown only when:
  // - Exactly one customer is selected
  // - No Groups filter is applied
  // - No Users filter is applied
  // Service/billing consistency is enforced in the backend via 400 responses.
  const canShowCustomerExportButtons =
    !!selectedCustomerIdForExport &&
    selectedGroupIds.length === 0 &&
    selectedUserIds.length === 0;

  const handleExportCustomerPdf = useCallback(async () => {
    if (!selectedCustomerIdForExport) return;
    setExportingCustomerPdf(true);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      const response = await api.get(
        `/billing/customers/${selectedCustomerIdForExport}/pdf-data?${params.toString()}`
      );
      const aggregate = response.data as CustomerBillingAggregate;

      generateCustomerBillingPdf({ aggregate });
    } catch (error: any) {
      console.error('Error exporting customer billing PDF:', error);
      const message =
        error?.response?.data?.message ||
        'Failed to export customer billing PDF. Please ensure billing configuration is consistent across all engagements.';
      showErrorModal(message);
    } finally {
      setExportingCustomerPdf(false);
    }
  }, [selectedCustomerIdForExport, selectedMonth]);

  const handleExportCustomerCsv = useCallback(async () => {
    if (!selectedCustomerIdForExport) return;
    setExportingCustomerCsv(true);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      const response = await api.get(
        `/billing/customers/${selectedCustomerIdForExport}/csv-data?${params.toString()}`
      );
      const aggregate = response.data as CustomerBillingAggregate;

      const { fromDate, toDate } = getBillingPeriodDates(aggregate.billingPeriod);
      const customerLabel = `"${aggregate.customerName.replace(/"/g, '""')}"`;
      const periodLabel = `${fromDate} - ${toDate}`;
      let grandTotal = 0;

      if (aggregate.fixedHours?.enabled) {
        const fh = aggregate.fixedHours;
        const csvRows: string[] = [];
        const minAmount = aggregate.summary.servicePlanFee || 0;
        const finalAmount = aggregate.summary.finalBill || 0;

        const escapeCsv = (val: string) => `"${val.replace(/"/g, '""')}"`;
        const engagementLabel = (r: { engagementNumber?: string; engagementName?: string }) =>
          escapeCsv(`${r.engagementNumber || ''} - ${r.engagementName || ''}`.trim());

        csvRows.push(`Customer,${customerLabel},Billing Period,"${periodLabel}"`);
        csvRows.push('');

        const includedRows = (fh.includedRows || []).filter((r) =>
          isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true })
        );
        if (includedRows.length > 0) {
          csvRows.push('Fixed Hours');
          csvRows.push('Engagement,Resource,Group,Included Hours,Rate,Value');
          includedRows.forEach((r) => {
            csvRows.push(
              [
                engagementLabel(r),
                escapeCsv(r.userName),
                escapeCsv(r.groupName),
                formatHours(r.includedHours),
                `${formatBillingMoneyPlain(0)}/hr`,
                formatBillingMoneyPlain(0),
              ].join(',')
            );
          });
          csvRows.push(
            ['Total', '', '', formatHours(fh.totalIncludedHours), '', formatBillingMoneyPlain(0)].join(',')
          );
          csvRows.push('');
        }

        if (fh.totalOverageHours > 0 && (fh.overageRows?.length ?? 0) > 0) {
          const overageRows = (fh.overageRows || []).filter((r) =>
            isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true })
          );
          if (overageRows.length > 0) {
            csvRows.push('Overage Charges');
            csvRows.push('Engagement,Resource,Group,Overage Hours,Rate,Overage Charges');
            overageRows.forEach((r) => {
              csvRows.push(
                [
                  engagementLabel(r),
                  escapeCsv(r.userName),
                  escapeCsv(r.groupName),
                  formatHours(r.overageHours),
                  `${formatBillingMoneyPlain(r.hourlyRate)}/hr`,
                  formatBillingMoneyPlain(r.billableAmount),
                ].join(',')
              );
            });
            csvRows.push(
              [
                'Total',
                '',
                '',
                formatHours(fh.totalOverageHours),
                '',
                formatBillingMoneyPlain(fh.totalOverageAmount),
              ].join(',')
            );
            csvRows.push('');
          }
        }

        csvRows.push('Customer Billing Summary');
        csvRows.push('Description,,Amount');
        if (minAmount > 0) {
          csvRows.push(
            ['Service Fee (Minimum Fee Adjustment)', '', formatBillingMoneyPlain(minAmount)].join(',')
          );
        }
        if (aggregate.summary.totalPlatformFee > 0) {
          csvRows.push(
            ['Platform Cost', '', formatBillingMoneyPlain(aggregate.summary.totalPlatformFee)].join(',')
          );
        }
        if (aggregate.summary.totalServiceFlatFee > 0) {
          csvRows.push(
            ['Service Plans', '', formatBillingMoneyPlain(aggregate.summary.totalServiceFlatFee)].join(',')
          );
        }
        if ((aggregate.summary.totalPatchingAmount || 0) > 0) {
          csvRows.push(
            [
              'Patching Services (Resource Costs)',
              '',
              formatBillingMoneyPlain(aggregate.summary.totalPatchingAmount),
            ].join(',')
          );
        }
        if ((fh.totalOverageAmount || 0) > 0) {
          csvRows.push(['Overage Charges', '', formatBillingMoneyPlain(fh.totalOverageAmount)].join(','));
        }
        csvRows.push(['Total', '', formatBillingMoneyPlain(finalAmount)].join(','));

        const csvContent = csvRows.join('\n');
        const [year, month] = aggregate.billingPeriod.split('-').map(Number);
        const date = new Date(year, month - 1, 1);
        const monthName = date.toLocaleDateString('en-US', { month: 'long' });
        const sanitizedCustomerName = aggregate.customerName.replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `${sanitizedCustomerName}-Customer-Billing-${monthName}-${year}.csv`;
        downloadCSV(csvContent, filename);
        return;
      }

      // Export only: exclude L1 Support Engineer rows from CSV
      const resourceChargesForExport = (aggregate.resourceCharges || []).filter((r) =>
        isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true })
      );

      // Build CSV rows for combined resource charges
      const headers = [
        'Customer',
        'Billing Period',
        'Charge Item Type',
        'Resource',
        'Group',
        'Total Hours',
        'Rate',
        'Total Amount',
      ];
      const csvRows: string[] = [headers.join(',')];

      resourceChargesForExport.forEach((r) => {
        const amount = r.billableAmount || 0;
        const hours = r.totalHours || 0;
        grandTotal += amount;
        const row = [
          `"${aggregate.customerName.replace(/"/g, '""')}"`,
          `${fromDate} - ${toDate}`,
          'resource_charges',
          `"${r.userName.replace(/"/g, '""')}"`,
          `"${r.groupName.replace(/"/g, '""')}"`,
          formatHours(hours),
          formatBillingMoneyPlain(r.hourlyRate),
          formatBillingMoneyPlain(amount),
        ];
        csvRows.push(row.join(','));
      });

      const ch = aggregate.complimentaryHours;
      if (ch && ch.hasServiceCreditEngagements) {
        (ch.resourceRows || []).forEach((r) => {
          csvRows.push(
            [
              `"${aggregate.customerName.replace(/"/g, '""')}"`,
              `${fromDate} - ${toDate}`,
              'complimentary_hours_resource',
              `"${r.userName.replace(/"/g, '""')}"`,
              `"${r.groupName.replace(/"/g, '""')}"`,
              formatHours(r.totalHours),
              formatBillingMoneyPlain(r.hourlyRate || 0),
              formatBillingMoneyPlain(r.billableAmount || 0),
            ].join(',')
          );
        });
        grandTotal += ch.totalOverageAmount || 0;
      }

      // Platform Fee row (if applicable)
      if (aggregate.summary.totalPlatformFee > 0) {
        const pf = aggregate.summary.totalPlatformFee;
        grandTotal += pf;
        csvRows.push(
          [
            `"${aggregate.customerName.replace(/"/g, '""')}"`,
            `${fromDate} - ${toDate}`,
            'platform_fee',
            '"Platform Fee"',
            '"Platform Fee"',
            '1',
            formatBillingMoneyPlain(pf),
            formatBillingMoneyPlain(pf),
          ].join(',')
        );
      }

      // Minimum Fee Adjustment (Service Fee) row
      const serviceFeeAdjustment = roundHalfUp2(
        Math.max(
          (aggregate.summary.servicePlanFee || 0) - (aggregate.summary.totalResourceCharges || 0),
          0
        )
      );
      if (serviceFeeAdjustment > 0) {
        grandTotal += serviceFeeAdjustment;
        csvRows.push(
          [
            `"${aggregate.customerName.replace(/"/g, '""')}"`,
            `${fromDate} - ${toDate}`,
            'service_fee_charges',
            '"Service Fee (Minimum Fee Adjustment)"',
            '"Service Fee (Minimum Fee Adjustment)"',
            '1',
            formatBillingMoneyPlain(serviceFeeAdjustment),
            formatBillingMoneyPlain(serviceFeeAdjustment),
          ].join(',')
        );
      }

      // Service Flat Fees row (if applicable)
      if (aggregate.summary.totalServiceFlatFee > 0) {
        const sf = aggregate.summary.totalServiceFlatFee;
        grandTotal += sf;
        csvRows.push(
          [
            `"${aggregate.customerName.replace(/"/g, '""')}"`,
            `${fromDate} - ${toDate}`,
            'service_flat_fee',
            '"Service Flat Fees"',
            '"Service Flat Fees"',
            '1',
            formatBillingMoneyPlain(sf),
            formatBillingMoneyPlain(sf),
          ].join(',')
        );
      }

      (aggregate.patchingServices || [])
        .filter((line) => line.showInPeriod !== false)
        .forEach((line) => {
          const laborTotal = (line.resourceBreakdown || []).reduce(
            (sum, r) => sum + (r.billableAmount || 0),
            0
          );
          grandTotal += laborTotal;
          const label = `"${aggregate.customerName.replace(/"/g, '""')}"`;
          csvRows.push(...formatPatchingCsvResourceRows(line, fromDate, toDate, label));
        });

      // Empty row and summary row
      csvRows.push('');
      csvRows.push(
        [
          '',
          '',
          'Total (Customer Summary)',
          '',
          '',
          '',
          '',
          formatBillingMoneyPlain(grandTotal),
        ].join(',')
      );

      const csvContent = csvRows.join('\n');
      // Format filename: Customer-Billing-customerName-monthName-Year.csv
      const [year, month] = aggregate.billingPeriod.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });
      const sanitizedCustomerName = aggregate.customerName.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `${sanitizedCustomerName}-Customer-Billing-${monthName}-${year}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error: any) {
      console.error('Error exporting customer billing CSV:', error);
      const message =
        error?.response?.data?.message ||
        'Failed to export customer billing CSV. Please ensure billing configuration is consistent across all engagements.';
      showErrorModal(message);
    } finally {
      setExportingCustomerCsv(false);
    }
  }, [selectedCustomerIdForExport, selectedMonth]);

  const toggleRowExpansion = (engagementId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(engagementId)) {
        newSet.delete(engagementId);
      } else {
        newSet.add(engagementId);
      }
      return newSet;
    });
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!canAccessBilling) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You do not have permission to view billing information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Error Modal */}
      <Transition appear show={!!errorModalMessage} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setErrorModalMessage(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-middle shadow-xl sm:p-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    Billing Validation Error
                  </Dialog.Title>
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">
                      {errorModalMessage}
                    </p>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setErrorModalMessage(null)}
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Monthly Billing</h1>
          <p className="mt-2 text-sm text-gray-700">
            View monthly billing summaries for all engagements based on recorded time entries.
          </p>
        </div>
        {canExportAll && (
          <div className="mt-4 sm:mt-0">
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={exportingAllCsv || exportingAllPdf}
                >
                  {(exportingAllCsv || exportingAllPdf) ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      Export All
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleExportAllCsv}
                          disabled={exportingAllCsv}
                          className={`${
                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          } flex items-center w-full px-4 py-2 text-sm disabled:opacity-50`}
                        >
                          <DocumentTextIcon className="h-5 w-5 mr-3" />
                          Export as CSV
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleExportAllPdf}
                          disabled={exportingAllPdf}
                          className={`${
                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          } flex items-center w-full px-4 py-2 text-sm disabled:opacity-50`}
                        >
                          <DocumentArrowDownIcon className="h-5 w-5 mr-3" />
                          Export as PDF
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        )}
      </div>

      {/* Month Selector */}
      <div className="mt-6 flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Billing Period:</span>
        <div className="w-64">
          <SingleSelectSearchableDropdown
            options={monthOptions}
            selectedId={selectedMonth}
            onChange={(id) => setSelectedMonth(id || monthOptions[0]?.id || '')}
            placeholder="Select Month"
            searchPlaceholder="Search months..."
            allowClear={false}
          />
        </div>
      </div>

      {/* Monthly Summary Cards (unfiltered - shows totals for the selected month) */}
      {monthlySummaryData?.summary && (
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Hours</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {formatHours(monthlySummaryData.summary.totalHours)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Agent Costs</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {formatCurrency(monthlySummaryData.summary.totalBillableAmount)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Platform Costs</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {formatCurrency(monthlySummaryData.summary.totalPlatformCost)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Service Flat Fee</dt>
                    <dd className="text-lg font-semibold text-purple-600">
                      {formatCurrency(monthlySummaryData.summary.totalServiceFlatFee || 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Billing</dt>
                    <dd className="text-lg font-semibold text-green-600">
                      {formatCurrency(monthlySummaryData.summary.totalFinalBill)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Filters */}
      <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-4 items-start">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-gray-400 mt-2" />
          <span className="text-sm font-medium text-gray-700 mt-2">Filter by:</span>
        </div>
        <div className="w-64">
          <MultiSelectSearchableDropdown
            options={customerOptions}
            selectedIds={selectedCustomerIds}
            onChange={setSelectedCustomerIds}
            placeholder="All Customers"
            searchPlaceholder="Search customers..."
            emptyMessage="No customers found"
          />
        </div>
        <div className="w-64">
          <MultiSelectSearchableDropdown
            options={groupOptions}
            selectedIds={selectedGroupIds}
            onChange={setSelectedGroupIds}
            placeholder="All Groups"
            searchPlaceholder="Search billing groups..."
            isLoading={isLoadingGroups}
            emptyMessage="No billing groups found"
          />
        </div>
        <div className="w-64">
          <MultiSelectSearchableDropdown
            options={userOptions}
            selectedIds={selectedUserIds}
            onChange={setSelectedUserIds}
            placeholder="All Users"
            searchPlaceholder="Search users..."
            isLoading={isLoadingUsers}
            emptyMessage="No users found"
          />
        </div>
      </div>

      {canShowCustomerExportButtons && billingData?.fixedHours?.enabled && (
        <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Fixed Hours Billing</h3>
          <p className="mt-1 text-xs text-gray-600">
            Customer fixed-hours pool: {formatHours(billingData.fixedHours.fixedHours)} · Remaining in pool:{' '}
            {formatHours(billingData.fixedHours.totalRemainingHours ?? 0)}
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full divide-y divide-indigo-200 text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                  <th className="py-2 pr-4">Engagement</th>
                  <th className="py-2 text-right">Fixed Pool</th>
                  <th className="py-2 text-right">Logged</th>
                  <th className="py-2 text-right">Included</th>
                  <th className="py-2 text-right">Remaining</th>
                  <th className="py-2 text-right">Overage</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-indigo-100">
                {(billingData.fixedHours.engagementRows || []).map((row) => (
                  <tr key={row.engagementId}>
                    <td className="py-2 pr-4 text-gray-900">
                      <div className="font-medium">{row.engagementNumber}</div>
                      <div className="text-xs text-gray-500">{row.engagementName}</div>
                    </td>
                    <td className="py-2 text-right text-gray-400">—</td>
                    <td className="py-2 text-right text-gray-900">{formatHours(row.loggedHours)}</td>
                    <td className="py-2 text-right text-gray-900">{formatHours(row.includedHours)}</td>
                    <td className="py-2 text-right text-gray-400">—</td>
                    <td className="py-2 text-right font-medium text-amber-700">
                      {formatHours(row.overageHours)}
                    </td>
                    <td className="py-2 text-right font-medium text-gray-900">
                      {formatCurrency(row.overageAmount, billingData.billings[0]?.currency || 'USD')}
                    </td>
                  </tr>
                ))}
                <tr className="bg-white/80 font-semibold">
                  <td className="py-2 pr-4">Total</td>
                  <td className="py-2 text-right text-indigo-900">
                    {formatHours(billingData.fixedHours.fixedHours)}
                  </td>
                  <td className="py-2 text-right">{formatHours(billingData.fixedHours.totalActualHours)}</td>
                  <td className="py-2 text-right">{formatHours(billingData.fixedHours.totalIncludedHours)}</td>
                  <td className="py-2 text-right text-emerald-700">
                    {formatHours(billingData.fixedHours.totalRemainingHours ?? 0)}
                  </td>
                  <td className="py-2 text-right text-amber-700">
                    {formatHours(billingData.fixedHours.totalOverageHours)}
                  </td>
                  <td className="py-2 text-right">
                    {formatCurrency(
                      billingData.fixedHours.totalOverageAmount,
                      billingData.billings[0]?.currency || 'USD'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canShowCustomerExportButtons &&
        billingData?.complimentaryHours &&
        billingData.complimentaryHours.hasServiceCreditEngagements && (
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900">Complimentary Hours</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full divide-y divide-emerald-200 text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                    <th className="py-2 pr-4">Engagement</th>
                    <th className="py-2 pr-4">Cycle</th>
                    <th className="py-2 text-right">Configured</th>
                    <th className="py-2 text-right">Available</th>
                    <th className="py-2 text-right">Logged</th>
                    <th className="py-2 text-right">Remaining</th>
                    <th className="py-2 text-right">Overage</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100">
                  {(billingData.complimentaryHours.engagementRows || []).map((row) => (
                    <tr key={row.engagementId}>
                      <td className="py-2 pr-4 text-gray-900">
                        <div className="font-medium">{row.engagementNumber}</div>
                        <div className="text-xs text-gray-500">{row.engagementName}</div>
                      </td>
                      <td className="py-2 pr-4 text-gray-700">
                        {formatYearMonthLabel(row.cycleStartMonth)} - {formatYearMonthLabel(row.cycleEndMonth)}
                      </td>
                      <td className="py-2 text-right font-medium text-gray-900">{formatHours(row.configuredComplimentaryHours)}</td>
                      <td className="py-2 text-right text-gray-900">{formatHours(row.availableHours)}</td>
                      <td className="py-2 text-right text-gray-900">{formatHours(row.loggedHours)}</td>
                      <td className="py-2 text-right text-gray-900">{formatHours(row.remainingHours)}</td>
                      <td className="py-2 text-right font-medium text-amber-700">{formatHours(row.overageHours)}</td>
                      <td className="py-2 text-right font-medium text-gray-900">
                        {formatCurrency(row.overageAmount, billingData.billings[0]?.currency || 'USD')}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/80 font-semibold">
                    <td className="py-2 pr-4" colSpan={2}>
                      Total
                    </td>
                    <td className="py-2 text-right">{formatHours(billingData.complimentaryHours.totalConfiguredHours || 0)}</td>
                    <td className="py-2 text-right">{formatHours(billingData.complimentaryHours.totalAvailableHours || 0)}</td>
                    <td className="py-2 text-right">{formatHours(billingData.complimentaryHours.totalLoggedHours || 0)}</td>
                    <td className="py-2 text-right">{formatHours(billingData.complimentaryHours.totalRemainingHours || 0)}</td>
                    <td className="py-2 text-right">{formatHours(billingData.complimentaryHours.totalOverageHours || 0)}</td>
                    <td className="py-2 text-right">
                      {formatCurrency(billingData.complimentaryHours.totalOverageAmount || 0, billingData.billings[0]?.currency || 'USD')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Performance Legend */}
      <div className="mt-6 flex items-center gap-6 text-sm">
        <span className="text-gray-600 font-medium">Effort vs Min Fee:</span>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-red-500"></span>
          <span className="text-gray-600">&lt; 50% (Critical)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-yellow-500"></span>
          <span className="text-gray-600">50-75% (Warning)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-green-500"></span>
          <span className="text-gray-600">&ge; 75% (Healthy)</span>
        </div>
      </div>

      {/* Billing Table */}
      <div className="mt-4 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center h-64 bg-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-64 bg-white">
                  <div className="text-center">
                    <p className="text-sm text-red-600">Error loading billing data</p>
                  </div>
                </div>
              ) : billingData?.billings?.length === 0 ? (
                <div className="flex items-center justify-center h-64 bg-white">
                  <div className="text-center">
                    <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No billing data</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No time entries recorded for the selected period.
                    </p>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Engagement
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Customer
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Hours
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Agent Costs
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-orange-800">
                        Patching Amount
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Min Fee
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Platform
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Service Flat
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Final Bill
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {billingsForTable.map((billing: EngagementBilling) => {
                      const performanceStatus = getBillingPerformanceStatus(billing.totalBillableAmount, billing.minimumServiceFee);
                      const isServiceCredit = billing.isServiceCreditEngagement;
                      const isFixedHoursRow = billing.isFixedHoursCustomer;
                      const isPatchingRow = isPatchingEngagementRow(billing);
                      const showQuarterBadge =
                        isPatchingRow &&
                        billing.patchingBillingCycle === 'QUARTERLY' &&
                        billing.patchingQuarterLabel;
                      return (
                      <Fragment key={billing.engagementId}>
                        <tr
                          className={`cursor-pointer border-l-4 ${
                            isServiceCredit
                              ? 'border-emerald-500 bg-emerald-50/50'
                              : isPatchingRow
                                ? 'border-orange-500 bg-orange-50/40'
                                : `${performanceStatus.borderColor} ${performanceStatus.bgColor}`
                          } hover:opacity-80`}
                          onClick={() => toggleRowExpansion(billing.engagementId)}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {expandedRows.has(billing.engagementId) ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400 mr-2" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400 mr-2" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {billing.engagementNumber}
                                  </span>
                                  {isServiceCredit ? (
                                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                                      Complimentary Hours
                                    </span>
                                  ) : isFixedHoursRow ? (
                                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                                      Fixed Hours
                                    </span>
                                  ) : isPatchingRow ? (
                                    <>
                                      <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                                        Patching
                                      </span>
                                      {showQuarterBadge && (
                                        <span
                                          className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800"
                                          title={
                                            billing.isPatchingQuarterBillingMonth
                                              ? 'Patching billed this month'
                                              : `Patching bill generates in ${billing.patchingQuarterLabel}`
                                          }
                                        >
                                          {billing.patchingQuarterLabel}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <PerformanceBadge
                                      actualBilling={billing.totalBillableAmount}
                                      minimumServiceFee={billing.minimumServiceFee}
                                    />
                                  )}
                                </div>
                                <div className="text-gray-500">{billing.engagementName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {billing.customerName}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                            {formatHours(billing.totalHours)}
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-4 text-sm text-right font-medium ${
                              isPatchingRow
                                ? 'text-orange-700'
                                : isFixedHoursRow
                                  ? billing.totalBillableAmount > 0
                                    ? 'text-amber-700'
                                    : 'text-gray-500'
                                  : performanceStatus.textColor
                            }`}
                          >
                            {formatCurrency(billing.totalBillableAmount, billing.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-medium text-orange-700">
                            {isPatchingEngagementRow(billing)
                              ? formatCurrency(getPatchingReferenceAmount(billing), billing.currency)
                              : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                            {formatCurrency(billing.minimumServiceFee, billing.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                            {formatCurrency(billing.platformCost, billing.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-purple-600 text-right">
                            {formatCurrency(billing.serviceFlatFee || 0, billing.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-green-600 text-right">
                            {formatCurrency(getDisplayFinalBill(billing), billing.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                            <BillingStatusBadge
                              engagementId={billing.engagementId}
                              billingPeriod={selectedMonth}
                              status={billingRecordsByEngagement.get(billing.engagementId)?.status || null}
                              onStatusUpdate={handleStatusUpdate}
                              isUpdating={updatingEngagementId === billing.engagementId}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportPdf(billing);
                                }}
                                disabled={exportingPdfEngagementId === billing.engagementId}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 border border-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download PDF for customer"
                              >
                                {exportingPdfEngagementId === billing.engagementId ? (
                                  <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                                ) : (
                                  <DocumentArrowDownIcon className="h-3 w-3" />
                                )}
                                PDF
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportEngagement(billing);
                                }}
                                disabled={exportingEngagementId === billing.engagementId}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Export CSV data"
                              >
                                {exportingEngagementId === billing.engagementId ? (
                                  <div className="animate-spin h-3 w-3 border-2 border-gray-500 border-t-transparent rounded-full" />
                                ) : (
                                  <ArrowDownTrayIcon className="h-3 w-3" />
                                )}
                                CSV
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(billing.engagementId) && (
                          <tr>
                            <td colSpan={11} className="px-4 py-4 bg-gray-50">
                              <EngagementBillingDetailRow
                                engagementId={billing.engagementId}
                                month={selectedMonth}
                                currency={billing.currency}
                                groupIds={selectedGroupIds}
                                userIds={selectedUserIds}
                                listFixedHoursBreakdown={billing.fixedHoursBreakdown}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                    })}
                    {(() => {
                      // Check if we should show total row: customer selected AND min fee/platform fee are the same across all rows
                      const shouldShowTotal = selectedCustomerIds.length > 0 && billingData?.billings && billingData.billings.length > 0;
                      
                      if (!shouldShowTotal) return null;

                      // Check if all rows have the same min fee, platform fee, and service flat fee
                      const firstBilling = billingData.billings[0];
                      const firstMinFee = firstBilling.minimumServiceFee;
                      const firstPlatformFee = firstBilling.platformCost;
                      const firstServiceFlatFee = firstBilling.serviceFlatFee || 0;
                      const firstCurrency = firstBilling.currency;

                      const allSameFees = billingData.billings.every(
                        (billing: EngagementBilling) =>
                          billing.minimumServiceFee === firstMinFee &&
                          billing.platformCost === firstPlatformFee
                      );

                      if (!allSameFees) return null;

                      // Use summary values from API (which uses customer-wise calculation)
                      const summary = billingData.summary;
                      if (!summary) return null;

                      const tablePatchingReferenceTotal = billingsForTable.reduce(
                        (sum, b) => sum + (isPatchingEngagementRow(b) ? getPatchingReferenceAmount(b) : 0),
                        0
                      );

                      // Get customer name from first billing
                      const customerName = firstBilling.customerName;

                      return (
                        <tr className="bg-gray-200 border-t-4 border-gray-600 shadow-sm">
                          <td colSpan={2} className="whitespace-nowrap py-5 pl-4 pr-3 text-base font-bold text-gray-900 sm:pl-6">
                            {customerName} Billing Summary
                          </td>
                          
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-gray-900 text-right">
                            {formatHours(summary.totalHours)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-gray-900 text-right">
                            {formatCurrency(summary.totalBillableAmount, firstCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-orange-700 text-right">
                            {tablePatchingReferenceTotal > 0
                              ? formatCurrency(tablePatchingReferenceTotal, firstCurrency)
                              : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-gray-900 text-right">
                            {formatCurrency(summary.totalMinimumServiceFee, firstCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-gray-900 text-right">
                            {formatCurrency(summary.totalPlatformCost, firstCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-purple-700 text-right">
                            {formatCurrency(summary.totalServiceFlatFee, firstCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-base font-bold text-green-700 text-right">
                            {formatCurrency(summary.totalFinalBill, firstCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm"></td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-center">
                            {canShowCustomerExportButtons && (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={handleExportCustomerPdf}
                                  disabled={exportingCustomerPdf}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 border border-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Export to PDF (Selected Customer)"
                                >
                                  {exportingCustomerPdf ? (
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                                  ) : (
                                    <DocumentArrowDownIcon className="h-3 w-3" />
                                  )}
                                  PDF
                                </button>
                                <button
                                  type="button"
                                  onClick={handleExportCustomerCsv}
                                  disabled={exportingCustomerCsv}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Export to CSV (Selected Customer)"
                                >
                                  {exportingCustomerCsv ? (
                                    <div className="animate-spin h-3 w-3 border-2 border-gray-500 border-t-transparent rounded-full" />
                                  ) : (
                                    <DocumentTextIcon className="h-3 w-3" />
                                  )}
                                  CSV
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Total count */}
      {billingData?.total !== undefined && billingData.total > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          Showing {billingData.billings?.length} of {billingData.total} engagements
        </div>
      )}
    </div>
  );
}

function agentBreakdownForDetail(detail: EngagementBillingDetail): AgentBillingBreakdown[] {
  if (detail.agentBreakdown && detail.agentBreakdown.length > 0) {
    return detail.agentBreakdown;
  }
  const fromPatching: AgentBillingBreakdown[] = [];
  for (const line of detail.patchingServices || []) {
    for (const row of line.resourceBreakdown || []) {
      fromPatching.push({
        userId: row.userId,
        userName: row.userName,
        email: row.email,
        groupId: row.groupId,
        groupName: row.groupName,
        hourlyRate: row.hourlyRate,
        rateSource: row.rateSource,
        totalMinutes: row.totalMinutes,
        totalHours: row.totalHours,
        billableAmount: line.showInPeriod === false ? 0 : row.billableAmount
      });
    }
  }
  return fromPatching.sort((a, b) => a.userName.localeCompare(b.userName));
}

// Expanded row component for agent breakdown
const EngagementBillingDetailRow: React.FC<{
  engagementId: string;
  month: string;
  currency: string;
  groupIds?: string[];
  userIds?: string[];
  listFixedHoursBreakdown?: EngagementFixedHoursBreakdown;
}> = ({ engagementId, month, currency, groupIds, userIds, listFixedHoursBreakdown }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing', 'detail', engagementId, month, groupIds, userIds],
    queryFn: async () => {
      const params = new URLSearchParams({ month });
      if (groupIds && groupIds.length > 0) {
        params.append('groupIds', groupIds.join(','));
      }
      if (userIds && userIds.length > 0) {
        params.append('userIds', userIds.join(','));
      }
      const response = await api.get(`/billing/engagements/${engagementId}?${params.toString()}`);
      return response.data as EngagementBillingDetail;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-4 text-sm text-red-600">
        Error loading agent breakdown
      </div>
    );
  }

  const agentRows = agentBreakdownForDetail(data);
  const hasAgentBreakdown = agentRows.length > 0;
  const hasFilters = (groupIds && groupIds.length > 0) || (userIds && userIds.length > 0);
  const patchingLines = data.patchingServices || [];
  const fh = data.fixedHoursBreakdown ?? listFixedHoursBreakdown;
  const isFixedHoursEngagement = fh?.enabled === true;
  const showIncludedHoursSection = isFixedHoursEngagement && (fh.includedRows?.length ?? 0) > 0;
  const showOverageSection =
    isFixedHoursEngagement && (fh.totalOverageHours ?? 0) > 0 && (fh.overageRows?.length ?? 0) > 0;

  return (
    <div className="overflow-hidden space-y-4">
      {hasAgentBreakdown ? (
      <>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Agent Breakdown</h4>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th scope="col" className="py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Agent
            </th>
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Group
            </th>
            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rate
            </th>
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rate Source
            </th>
            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Hours
            </th>
            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cost
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {agentRows.map((agent) => (
            <tr key={`${agent.userId}-${agent.groupId}`}>
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm">
                <div className="font-medium text-gray-900">{agent.userName}</div>
                <div className="text-gray-500 text-xs">{agent.email}</div>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                {agent.groupName}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-right">
                {formatCurrency(agent.hourlyRate, currency)}/hr
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm">
                <RateSourceBadge source={agent.rateSource} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-right">
                {formatHours(agent.totalHours)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900 text-right">
                {formatCurrency(agent.billableAmount, currency)}
              </td>
            </tr>
          ))}
        </tbody>
        {hasFilters && (
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan={4} className="py-2 pl-4 pr-3 text-sm font-medium text-gray-900">
                Filtered Total
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900 text-right">
                {formatHours(data.summary?.totalHours ?? 0)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900 text-right">
                {formatCurrency(data.summary?.totalBillableAmount || 0, currency)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
      </>
      ) : (
        <p className="text-sm text-gray-500 py-2">No agent time entries for this period</p>
      )}

      {showIncludedHoursSection && fh && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Fixed Hours</h4>
          <table className="min-w-full divide-y divide-indigo-100 text-sm">
            <thead className="bg-indigo-100/60">
              <tr>
                <th className="py-2 pl-3 pr-3 text-left text-xs font-medium text-gray-600 uppercase">Resource</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Group</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Included Hours</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Rate</th>
                <th className="py-2 pr-3 text-right text-xs font-medium text-gray-600 uppercase">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-50 bg-white">
              {fh.includedRows.map((row) => (
                <tr key={`${row.userId}-${row.groupId}-inc`}>
                  <td className="py-2 pl-3 pr-3 text-gray-900">{row.userName}</td>
                  <td className="px-3 py-2 text-gray-700">{row.groupName}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{formatHours(row.includedHours)}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(row.hourlyRate, currency)}/hr</td>
                  <td className="py-2 pr-3 text-right font-medium text-gray-900">{formatCurrency(0, currency)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 pl-3 pr-3" colSpan={2}>Total</td>
                <td className="px-3 py-2 text-right">{formatHours(fh.totalIncludedHours)}</td>
                <td className="px-3 py-2 text-right" />
                <td className="py-2 pr-3 text-right">{formatCurrency(0, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {showOverageSection && fh && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Overage Charges</h4>
          <table className="min-w-full divide-y divide-emerald-100 text-sm">
            <thead className="bg-emerald-500">
              <tr>
                <th className="py-2 pl-3 pr-3 text-left text-xs font-medium text-white uppercase">Resource</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-white uppercase">Group</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-white uppercase">Overage Hours</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-white uppercase">Resource Rate</th>
                <th className="py-2 pr-3 text-right text-xs font-medium text-white uppercase">Overage Charges</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-50 bg-white">
              {fh.overageRows.map((row) => (
                <tr key={`${row.userId}-${row.groupId}-ov`}>
                  <td className="py-2 pl-3 pr-3 text-gray-900">{row.userName}</td>
                  <td className="px-3 py-2 text-gray-700">{row.groupName}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">{formatHours(row.overageHours)}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(row.hourlyRate, currency)}/hr</td>
                  <td className="py-2 pr-3 text-right font-medium text-gray-900">{formatCurrency(row.billableAmount, currency)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 pl-3 pr-3" colSpan={2}>Total</td>
                <td className="px-3 py-2 text-right">{formatHours(fh.totalOverageHours)}</td>
                <td className="px-3 py-2 text-right" />
                <td className="py-2 pr-3 text-right">{formatCurrency(fh.totalOverageAmount, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!hasFilters && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Billing Charges</h4>
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-100">
              <tr>
                <td className="py-2 pl-4 pr-3 text-sm text-gray-500">Minimum Service Fee</td>
                <td className="py-2 pr-4 text-sm font-medium text-gray-900 text-right">
                  {formatCurrency(data.summary?.minimumServiceFee || 0, currency)}
                </td>
              </tr>
              {!isFixedHoursEngagement && (
              <tr>
                <td className="py-2 pl-4 pr-3 text-sm text-gray-500">
                  {data.engagement?.isServiceCreditEngagement
                    ? 'Overage labor (applied service fee)'
                    : 'Applied Service Fee (max of agent costs and min fee)'}
                </td>
                <td className="py-2 pr-4 text-sm font-medium text-gray-900 text-right">
                  {formatCurrency(data.summary?.appliedServiceFee || 0, currency)}
                </td>
              </tr>
              )}
              {showOverageSection && fh && fh.totalOverageAmount > 0 && (
                <tr>
                  <td className="py-2 pl-4 pr-3 text-sm text-emerald-700">Overage Charges</td>
                  <td className="py-2 pr-4 text-sm font-medium text-emerald-700 text-right">
                    {formatCurrency(fh.totalOverageAmount, currency)}
                  </td>
                </tr>
              )}
              <tr>
                <td className="py-2 pl-4 pr-3 text-sm text-gray-500">Platform Cost</td>
                <td className="py-2 pr-4 text-sm font-medium text-gray-900 text-right">
                  {formatCurrency(data.summary?.platformCost || 0, currency)}
                </td>
              </tr>
              <tr>
                <td className="py-2 pl-4 pr-3 text-sm text-purple-600">
                  Service Flat Fee
                  {data.serviceFlatFeeDetails && data.serviceFlatFeeDetails.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1 font-normal">
                      {data.serviceFlatFeeDetails.map((plan) => (
                        <div key={plan.planId}>
                          {plan.planName}: {formatCurrency(plan.price, currency)}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-4 text-sm font-medium text-purple-600 text-right align-top">
                  {formatCurrency(data.summary?.serviceFlatFee || 0, currency)}
                </td>
              </tr>
              {patchingLines.map((line) => {
                const quarterCycle = formatPatchingQuarterCycleLabel(
                  line.quarterCycleStartMonth,
                  line.quarterCycleEndMonth
                );
                const monthDetail =
                  line.quarterCycleMonths && line.quarterCycleMonths.length > 0
                    ? line.quarterCycleMonths
                        .map((m) => {
                          const hrs =
                            line.resourceBreakdown?.reduce((sum, r) => {
                              const mh = r.monthlyHours?.find((x) => x.month === m);
                              return sum + (mh?.totalHours ?? 0);
                            }, 0) ?? 0;
                          return `${formatPatchingBillingMonthLabel(m)}: ${formatHours(hrs)}`;
                        })
                        .join(' · ')
                    : null;
                return (
                <tr key={line.patchingType}>
                  <td className="py-2 pl-4 pr-3 text-sm text-orange-700">
                    + {line.patchingTypeLabel} ({line.billingCycleLabel})
                    <span className="block text-xs text-gray-500 font-normal">
                      {line.billingCycle === 'QUARTERLY' && quarterCycle && (
                        <>Quarter {quarterCycle} · </>
                      )}
                      Total logged hours: {formatHours(line.loggedHours)}
                      {monthDetail && <> · {monthDetail}</>}
                      {' '}
                      · Resource billed: {formatCurrency(line.totalAmount, currency)} · Ref:{' '}
                      {formatCurrency(line.billingAmount, currency)}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-sm font-medium text-orange-700 text-right">
                    {formatCurrency(line.totalAmount, currency)}
                  </td>
                </tr>
              );
              })}
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="py-2 pl-4 pr-3 text-sm font-semibold text-gray-900">
                  {data.summary?.isPatchingEngagement ? 'Final Bill (patching only)' : 'Final Bill'}
                </td>
                <td className="py-2 pr-4 text-sm font-semibold text-green-600 text-right">
                  {formatCurrency(getDetailDisplayFinalBill(data), currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
